import { useEffect, useState } from 'react';
import { usePaytaxCalcContext } from './context';
import { TaxCalculation } from './tax-calculation';
import { CALCULATOR_TAB_PROPS, taxRegimeMap } from './types';
import { generateIncomeLabel, getFinalMaxValue } from './utils';

export const GraphCalculation = ({
  taxCalculation,
  activeTab,
}: CALCULATOR_TAB_PROPS) => {
  const regime = taxRegimeMap[activeTab || 0];
  const [targetHeight, setTargetHeight] = useState(220);
  const { financialYearCalc } = usePaytaxCalcContext();
  useEffect(() => {
    if (window) {
      const viewPortWidth = window?.innerWidth;
      const updateHeight = () => {
        if (viewPortWidth < 768) {
          setTargetHeight(184);
        } else if (viewPortWidth >= 768 && viewPortWidth <= 1024) {
          setTargetHeight(160);
        } else {
          setTargetHeight(220);
        }
      };
      updateHeight();
    }
  }, []);

  const regimeData = regime && taxCalculation?.[regime];
  if (!regimeData) {
    return <></>;
  }
  const {
    exemptionsAndDeductionsInSalary,
    standardDeductions,
    taxableIncome,
    totalIncome,
    grossTax,
  } = regimeData;
  const steps = 4;
  const roundedMaxValue = getFinalMaxValue(Number(totalIncome));
  const incomeLabels = generateIncomeLabel(steps, roundedMaxValue);

  const calculatePercetage = (
    base: number,
    value: number,
    graphBase: number
  ) => {
    const percentage = Math.floor((Number(value) / Number(base)) * 100);
    const graphHeight = Math.floor((graphBase * percentage) / 100);
    const defaultHeight = 4;
    return graphHeight ? graphHeight : defaultHeight;
  };

  return (
    <>
      <div className="flex justify-between sm:block gap-16 md:gap-5 mb-6 sm:mb-0">
        <div className="graphTabWrapper mt-6 w-[343px] min-w-[320px] md:min-w-[247px] sm:mt-14 sm:w-full">
          <div className="bg-[url('https://assets.clear.in/cleartax/images/1724840642_group18142194442.png')] bg-no-repeat bg-bottom bg-contain flex justify-end  sm:justify-center gap-4 items-end mt-10 sm:mt-6 md:mt-5 h-[217px] md:h-[160px] sm:h-[185px] relative pr-10 max-w-[324px]  ">
            <div
              className="animate-height w-11 sm:w-8 md:w-8 rounded-t bg-blue-100"
              title={totalIncome}
              style={
                {
                  '--target-height': `${calculatePercetage(
                    parseInt(totalIncome),
                    parseInt(totalIncome),
                    targetHeight
                  )}px`,
                } as React.CSSProperties
              }
            ></div>
            <div
              className="animate-height w-11 sm:w-8 md:w-7 rounded-t bg-blue-450"
              title={String(
                parseInt(exemptionsAndDeductionsInSalary) +
                  parseInt(standardDeductions)
              )}
              style={
                {
                  '--target-height': `${calculatePercetage(
                    parseInt(totalIncome),
                    parseInt(exemptionsAndDeductionsInSalary) +
                      parseInt(standardDeductions),
                    targetHeight
                  )}px`,
                } as React.CSSProperties
              }
            ></div>
            <div
              className="animate-height w-11 sm:w-8 md:w-7 rounded-t bg-blue-200"
              style={
                {
                  '--target-height': `${calculatePercetage(
                    parseInt(totalIncome),
                    parseInt(taxableIncome),
                    targetHeight
                  )}px`,
                } as React.CSSProperties
              }
            ></div>
            <div
              className="animate-height w-11 sm:w-8 md:w-7 rounded-t bg-green-250"
              style={
                {
                  '--target-height': `${calculatePercetage(
                    parseInt(totalIncome),
                    parseInt(grossTax),
                    targetHeight
                  )}px`,
                } as React.CSSProperties
              }
            ></div>
            <>
              <div className="absolute left-0 text-s-10 md:text-s-8 text-grey-700 bottom-[54px] sm:bottom-[46px] md:bottom-[40px]">
                ₹{incomeLabels[1]}
              </div>
              <div className="absolute left-0 text-s-10 md:text-s-8 text-grey-700 bottom-[108px] sm:bottom-[92px] md:bottom-[80px]">
                ₹{incomeLabels[2]}
              </div>
              <div className="absolute left-0 text-s-10 md:text-s-8 text-grey-700 bottom-[162px] sm:bottom-[138px] md:bottom-[120px]">
                ₹{incomeLabels[3]}
              </div>
              <div className="absolute left-0 text-s-10 md:text-s-8 text-grey-700 bottom-[215px] sm:bottom-[185px] md:bottom-[160px]">
                ₹{incomeLabels[4]}
              </div>
            </>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-12 sm:mt-6 sm:gap-2 sm:gap-x-0 ">
            <div className="flex gap-2 items-center">
              <div className="w-[18px] h-[18px] rounded-sm bg-blue-100 "></div>
              <div className="text-s-14 font-medium sm:text-s-12">
                Total income
              </div>
            </div>
            <div className="flex gap-2 items-center">
              <div className="w-[18px] h-[18px] rounded-sm bg-blue-200"></div>
              <div className="text-s-14 font-medium sm:text-s-12">
                Taxable income
              </div>
            </div>
            <div className="flex gap-2 items-center">
              <div className="w-[18px] h-[18px] rounded-sm bg-blue-450"></div>
              <div className="text-s-14 font-medium sm:text-s-12">
                Deduction
              </div>
            </div>
            <div className="flex gap-2 items-center">
              <div className="w-[18px] h-[18px] rounded-sm bg-green-250"></div>
              <div className="text-s-14 font-medium sm:text-s-12">
                Tax payable
              </div>
            </div>
          </div>
        </div>
        <TaxCalculation taxCalculation={taxCalculation} activeTab={activeTab} />
      </div>
      <p className="text-s-14 text-font-500 sm:text-s-12 sm:leading-snug">
        ITR filing due date:{' '}
        <span className="font-bold sm:text-s-12">
          {financialYearCalc === '2026'
            ? 'ITR 1/2 - July 31 and ITR 3/4 - Aug 31'
            : 'ITR 1/2 - July 31 and ITR 3/4 - Aug 31'}{' '}
        </span>
      </p>
    </>
  );
};
